// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyProduct } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type SortProductsPopupDetails = {
    categoryId: string;
    products: TyProduct[];
    onSuccess: () => void;
};

type Props = {
    onClose: () => void;
    details?: SortProductsPopupDetails;
};

type TyGhostInfo = {
    product: TyProduct;
    startY: number;
    x: number;
    width: number;
    height: number;
};

// --- Logic --------------------------------------------------------------------------------------

export default function SortProductsPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const categoryId = details?.categoryId ?? "";

    // --- Items ----------------------------------------------------------------------------------

    const [items, setItems] = useState<TyProduct[]>(() => details?.products ?? []);
    const originalRef = useRef<TyProduct[]>(details?.products ?? []);

    // --- FLIP animation -------------------------------------------------------------------------

    const itemElemsRef = useRef<Map<string, HTMLLIElement>>(new Map());
    const prevPositionsRef = useRef<Map<string, number>>(new Map());
    const needsFlipRef = useRef(false);

    const snapshotPositions = () => {
        const map = new Map<string, number>();
        itemElemsRef.current.forEach((el, id) => map.set(id, el.getBoundingClientRect().top));
        prevPositionsRef.current = map;
        needsFlipRef.current = true;
    };

    useLayoutEffect(() => {
        if (!needsFlipRef.current) return;
        needsFlipRef.current = false;

        const draggingItemId = dragRef.current !== null ? items[dragRef.current.index]?._id : null;

        itemElemsRef.current.forEach((el, id) => {
            if (id === draggingItemId) return;
            const prevTop = prevPositionsRef.current.get(id);
            if (prevTop === undefined) return;
            const newTop = el.getBoundingClientRect().top;
            const delta = prevTop - newTop;
            if (Math.abs(delta) < 1) return;

            el.style.transition = "none";
            el.style.transform = `translateY(${delta}px)`;
            void el.offsetHeight;
            el.style.transition = "transform 0.18s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
            el.style.transform = "";
        });
    }, [items]);

    // --- Drag -----------------------------------------------------------------------------------

    const dragRef = useRef<{ index: number; offsetY: number; pointerId: number } | null>(null);
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [ghostInfo, setGhostInfo] = useState<TyGhostInfo | null>(null);
    const ghostRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const onHandlePointerDown = (e: React.PointerEvent<HTMLElement>, index: number) => {
        e.preventDefault();
        const li = (e.currentTarget as HTMLElement).closest<HTMLLIElement>("li");
        if (!li || !listRef.current) return;

        const rect = li.getBoundingClientRect();
        // eslint-disable-next-line react-hooks/immutability
        dragRef.current = { index, offsetY: e.clientY - rect.top, pointerId: e.pointerId };
        listRef.current.setPointerCapture(e.pointerId);
        setDraggingId(items[index]._id);
        setGhostInfo({ product: items[index], startY: rect.top, x: rect.left, width: rect.width, height: rect.height });
    };

    const onListPointerMove = (e: React.PointerEvent<HTMLUListElement>) => {
        const drag = dragRef.current;
        if (!drag || !listRef.current) return;

        if (ghostRef.current) ghostRef.current.style.top = `${e.clientY - drag.offsetY}px`;

        const lis = listRef.current.querySelectorAll<HTMLLIElement>(":scope > li");
        let newIndex = lis.length - 1;
        for (let i = 0; i < lis.length; i++) {
            const r = lis[i].getBoundingClientRect();
            if (e.clientY < r.top + r.height / 2) {
                newIndex = i;
                break;
            }
        }

        if (newIndex !== drag.index) {
            snapshotPositions();
            const from = drag.index;
            setItems((prev) => {
                const arr = [...prev];
                const [moved] = arr.splice(from, 1);
                arr.splice(newIndex, 0, moved);
                return arr;
            });
            // eslint-disable-next-line react-hooks/immutability
            drag.index = newIndex;
        }
    };

    const stopDrag = () => {
        if (dragRef.current && listRef.current) {
            try {
                listRef.current.releasePointerCapture(dragRef.current.pointerId);
            } catch {
                // pointer already released
            }
        }
        // eslint-disable-next-line react-hooks/immutability
        dragRef.current = null;
        setDraggingId(null);
        setGhostInfo(null);
    };

    // --- Submit ---------------------------------------------------------------------------------

    const {
        data: submitData,
        loading: submitting,
        invoke: submitInvoke,
    } = useFetch<TyApiResponse<Record<string, never>>>(`/menu/categories/${categoryId}`, { method: "PUT" }, false);

    useEffect(() => {
        if (!submitData?.success) return;
        details?.onSuccess();
        onClose();
    }, [submitData]);

    const handleSave = () => {
        if (items.length === 0) return;
        submitInvoke({ data: { productIds: items.map((p) => p._id) } });
    };

    const handleReset = () => {
        snapshotPositions();
        setItems([...originalRef.current]);
    };

    // --- Render ---------------------------------------------------------------------------------

    return (
        <div className="sort-products-popup">
            <div className="sort-products-popup-header">
                <p className="sort-products-popup-title">{t.sortProducts.title}</p>
                <button type="button" className="sort-products-popup-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <ul
                ref={listRef}
                className="sort-products-popup-list"
                onPointerMove={onListPointerMove}
                onPointerUp={stopDrag}
                onPointerCancel={stopDrag}
            >
                {items.map((item, index) => (
                    <li
                        key={item._id}
                        ref={(el) => {
                            if (el) itemElemsRef.current.set(item._id, el);
                            else itemElemsRef.current.delete(item._id);
                        }}
                        className={`sort-products-row${draggingId === item._id ? " sort-products-row--placeholder" : ""}`}
                    >
                        <div className="sort-products-row-handle" onPointerDown={(e) => onHandlePointerDown(e, index)}>
                            <img src="/icons/dragging-icon.svg" draggable="false" alt="" width={20} height={20} />
                        </div>
                        <div className="sort-products-row-thumb">
                            {item.pictures[0] ? (
                                <img src={item.pictures[0]} draggable={false} alt="" className="sort-products-row-thumb-img" />
                            ) : (
                                <div className="sort-products-row-thumb-placeholder">
                                    <img src="/icons/category-icon.svg" alt="" width={16} height={16} />
                                </div>
                            )}
                        </div>
                        <p className="sort-products-row-name">{item.name}</p>
                    </li>
                ))}
            </ul>

            <div className="sort-products-popup-footer">
                <button type="button" className="sort-products-popup-reset" onClick={handleReset} disabled={submitting}>
                    {t.sortProducts.reset}
                </button>
                <button type="button" className="sort-products-popup-save" onClick={handleSave} disabled={submitting || items.length === 0}>
                    {t.sortProducts.save}
                </button>
            </div>

            {ghostInfo &&
                createPortal(
                    <div
                        ref={ghostRef}
                        style={{
                            position: "fixed",
                            left: ghostInfo.x,
                            top: ghostInfo.startY,
                            width: ghostInfo.width,
                            height: ghostInfo.height,
                            pointerEvents: "none",
                            zIndex: 99999,
                        }}
                    >
                        <div className="sort-products-ghost-row">
                            <div className="sort-products-row-handle sort-products-row-handle--active">
                                <img src="/icons/dragging-icon.svg" draggable="false" alt="" width={20} height={20} />
                            </div>
                            <div className="sort-products-row-thumb">
                                {ghostInfo.product.pictures[0] ? (
                                    <img src={ghostInfo.product.pictures[0]} draggable={false} alt="" className="sort-products-row-thumb-img" />
                                ) : (
                                    <div className="sort-products-row-thumb-placeholder">
                                        <img src="/icons/category-icon.svg" alt="" width={16} height={16} />
                                    </div>
                                )}
                            </div>
                            <p className="sort-products-row-name">{ghostInfo.product.name}</p>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
